import styled from 'styled-components';
import {getCategoryIcon} from '@/components/CategoryIcon';
import {LoadingBar} from '@/components/LoadingBar';
import {AnalysisItem, KodataCategoryGroup} from '@/map/@types';
import {useStoreHallSalesCategoy} from '@/map/api/analytics/hall-sales';
import {ColorCircleWithIcon} from '@/map/components/Markers/ColorCircleWithIcon';
import {convertToKodataCategory} from '@/map/utils';
import {Divider, Flexbox, PaddingBox} from '@/styled-components';
import {palette} from '@/styles';
import {getCategoryId, humanizeNumber, withComma} from '@/utils';
import {SubText, ValueFormatText, ValueText} from '../StyledComponents';

interface IProps {
  item: AnalysisItem;
  category: KodataCategoryGroup;
}

export const CategorySalesItem = ({item, category}: IProps) => {
  const {error, isLoading, data} = useStoreHallSalesCategoy({
    item,
    categories: [category],
  });

  if (!category) return null;

  const cat1Depth = convertToKodataCategory(category.category);

  const categoryId = getCategoryId(cat1Depth);

  const icon = getCategoryIcon(categoryId, 22);

  const result = data?.[0];
  const increaseColor =
    result &&
    (result.increaseRate > 0
      ? palette.red100
      : result.increaseRate < 0
      ? palette.blue100
      : undefined);

  return (
    <Root>
      <Row>
        <ColorCircleWithIcon
          icon={icon}
          placeholder={category.category}
          size="large"
        />
        <SubText fontWeight="700" style={{flexGrow: 1, flexBasis: 0}}>
          {category.categoryDetail}
        </SubText>
        {isLoading ? (
          <LoadingBar size={14} color={palette.textSecondary} />
        ) : !!error || !result?.totalSales ? (
          <ValueText opacity={0.5}>표시 할 수 없는 지역입니다</ValueText>
        ) : (
          <>
            <ValueText>{humanizeNumber(result.totalSales, true)}</ValueText>
            <ValueFormatText>원</ValueFormatText>
          </>
        )}
      </Row>
      {!!result?.totalSales && (
        <>
          <PaddingBox top={4} bottom={4}>
            <Divider />
          </PaddingBox>
          <Row>
            <SubText>월평균</SubText>
            <SubText color={palette.textPrimary} fontWeight="700">
              {!!result.averageSales
                ? `${humanizeNumber(result.averageSales, true)}원`
                : '-'}
            </SubText>
            <SubText color={palette.gray150}>|</SubText>
            <SubText>총 매장수</SubText>
            <SubText color={palette.textPrimary} fontWeight="700">
              {!!result.storeCount ? `${withComma(result.storeCount)}개` : '-'}
            </SubText>
            <SubText color={palette.gray150}>|</SubText>
            <SubText>전월 대비</SubText>
            <SubText color={increaseColor} fontWeight="700">
              {!!result.increaseRate
                ? `${withComma(Math.abs(result.increaseRate))}%`
                : ''}
              {result.increaseRate > 0
                ? '▲'
                : result.increaseRate < 0
                ? '▼'
                : '-'}
            </SubText>
          </Row>
        </>
      )}
    </Root>
  );
};

const Root = styled.div`
  padding: 4px 4px 4px 8px;
`;

const Row = styled(Flexbox)`
  gap: 4px;
  justify-content: center;

  & > * {
    flex-shrink: 0;
  }
`;
